import { IInitializableAdapter } from './base.adapter';
import { IIdentityProvider, DefaultIdentityProvider } from './identity-provider';
import { AdapterRegistry, IAdapterConfig, IAdaptation } from './adapter-registry';
import { IModelAdapterAsync } from './model-adapter-async';
import { IModelAdapter } from './model-adapter';
import { IModel, IResourceContext } from '../base/annotation';
import { DataFlowDSLAdapterContext } from './dataflow.dsl-adapter-context';
import { ROOT_RESOURCE_CONTEXT_DATA } from '../../constants/context';

export class AdapterContext {
    private static readonly _defaultContexts: Map<string, AdapterContext> = new Map<string, AdapterContext>();

    /**
     * Default adapter context with default identity provider and the latest version
     *
     * @deprecated Since we want to enable multiple contexts, we should avoid using any static properties.
     * Use defaultContextOf instead.
     */
    public static readonly defaultContext = AdapterContext.newAdapterContext({
        contextData: ROOT_RESOURCE_CONTEXT_DATA,
        identityProvider: DefaultIdentityProvider.instance
    });

    /**
     * get `defaultContext` of a context
     * @param contextData
     */
    public static defaultContextOf(contextData: IResourceContext): AdapterContext {
        if (!this._defaultContexts.has(contextData.contextId)) {
            this._defaultContexts.set(contextData.contextId, AdapterContext.newAdapterContext({ contextData }));
        }
        return this._defaultContexts.get(contextData.contextId);
    }

    public readonly removeEncryptedCredentials: boolean = false;
    public readonly usedForQueryApi: boolean = false;
    public readonly identityProvider: IIdentityProvider;
    public readonly dataflowDSLAdapterContext: DataFlowDSLAdapterContext;
    public writeIdentities: boolean;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private modelToAdapters = new Map<IModel, IModelAdapter<any, any, any>>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private asyncModelToAdapters = new Map<IModel, IModelAdapterAsync<any, any, any>>();

    public get contextData(): IResourceContext {
        return this.adapterConfig.contextData;
    }

    public constructor(public adapterConfig: IAdapterConfig) {
        this.identityProvider = adapterConfig.identityProvider ?? DefaultIdentityProvider.instance;
        this.removeEncryptedCredentials = adapterConfig.removeEncryptedCredentials
            ? adapterConfig.removeEncryptedCredentials
            : false;
        this.usedForQueryApi = adapterConfig.usedForQueryApi ? adapterConfig.usedForQueryApi : false;
        this.dataflowDSLAdapterContext = new DataFlowDSLAdapterContext(adapterConfig.contextData);
    }

    public static newAdapterContext(adapterConfig: IAdapterConfig): AdapterContext {
        return new AdapterContext(adapterConfig);
    }

    public static getIdentity(ownerIdentity: string, propertyName: string): string {
        return `${ownerIdentity.toLowerCase()}$$${propertyName.toLowerCase()}`;
    }

    public isAsyncAdapter(model: IModel): boolean {
        const adapter = this.modelToAdapters.has(model);
        const asyncAdapter = this.asyncModelToAdapters.has(model);

        if (!adapter && !asyncAdapter) {
            this.createAdapter(model);
        }
        return this.asyncModelToAdapters.has(model);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public adapterFor<T = any, U = any, D = any>(model: IModel): IModelAdapter<T, U, D> | undefined {
        const adapter = this.modelToAdapters.get(model);
        if (adapter) {
            return adapter;
        }
        this.createAdapter(model);
        return this.modelToAdapters.get(model);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public asyncAdapterFor<T = any, U = any, D = any>(model: IModel): IModelAdapterAsync<T, U, D> | undefined {
        const adapter: IModelAdapterAsync<T, U, D> = this.asyncModelToAdapters.get(model);
        if (adapter) {
            return adapter;
        }
        this.createAdapter(model); // It might do nothing
        return this.asyncModelToAdapters.get(model); // Then this can still be undefined if above line does nothing
    }

    public adapterSpecFor(model: IModel): {} | undefined {
        const adaptation = this.matchingAdaptation(model);

        return adaptation ? adaptation.adapterDefinition.adapts : undefined;
    }

    /**
     * Resets the adapter config to default values, clearing migration-specific flags.
     * Should be called after migration operations complete to prevent stale flags from affecting
     * other flows like mountedDataFactory and Synapse applications.
     */
    public resetAdapterConfig(): void {
        const contextData = this.adapterConfig.contextData;
        this.adapterConfig = {
            contextData,
            convertToFabric: false,
            inlineDataset: false,
            referenceById: false,
            linkedServiceNameToExternalConnectionId: undefined,
            postProcessResource: undefined,
            preProcessResource: undefined
        };
    }

    private matchingAdaptation(model: IModel): IAdaptation | undefined {
        // Look at the spec and pick the right adapter to create. There can only be one
        const adaptations = AdapterRegistry.adaptations(model);

        if (!adaptations) {
            return undefined;
        }

        // Find the best version. No version requested is default. If specific version
        // requested and not available fall back to default
        // eslint-disable-next-line no-undef-init
        let matchAdaptation: IAdaptation | undefined;
        for (const adaptation of adaptations) {
            if (adaptation.adapterDefinition.version === this.adapterConfig.version) {
                matchAdaptation = adaptation;
                break;
            } else if (!adaptation.adapterDefinition.version) {
                matchAdaptation = adaptation;
            }
        }

        return matchAdaptation;
    }

    private createAdapter(model: IModel): void {
        const matchAdaptation = this.matchingAdaptation(model);
        if (!matchAdaptation) {
            return;
        }

        if (matchAdaptation.adapterDefinition.asynchronous) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const adapter = new (matchAdaptation.adapterConstructor as any)() as IModelAdapterAsync<any, any>;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const initializableAdapter = adapter as any as IInitializableAdapter;
            if (initializableAdapter.initContext) {
                initializableAdapter.initContext(this);
            }
            this.asyncModelToAdapters.set(model, adapter);
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const adapter = new (matchAdaptation.adapterConstructor as any)() as IModelAdapter<any, any>;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const initializableAdapter = adapter as any as IInitializableAdapter;
            if (initializableAdapter.initContext) {
                initializableAdapter.initContext(this);
            }
            this.modelToAdapters.set(model, adapter);
        }
    }
}
